import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FaArrowUp } from 'react-icons/fa';
import Section from './layout/Section';
import ScrollReveal from './animations/ScrollReveal';
import { experience } from '../data/profile';

export default function Experience() {
  const [activeIndex, setActiveIndex] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const active = experience[activeIndex];

  const handleKeyDown = (event) => {
    const lastIndex = experience.length - 1;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveIndex((current) => (current === lastIndex ? 0 : current + 1));
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveIndex((current) => (current === 0 ? lastIndex : current - 1));
    }
  };

  return (
    <Section
      id="experience"
      eyebrow="Experience"
      title="Where I’ve worked"
      lead="Six years of shipping as the engineer who owns a thing end to end. Pick a role to see what it involved."
      className="bg-slate-50 dark:bg-slate-900"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-dotted opacity-40" />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,18rem)_1fr] lg:gap-12">
        {/* Role selector */}
        <ScrollReveal direction="up">
          <div
            role="tablist"
            aria-label="Work history"
            aria-orientation="vertical"
            onKeyDown={handleKeyDown}
            className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0"
          >
            {experience.map((role, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={role.company}
                  type="button"
                  role="tab"
                  id={`experience-tab-${index}`}
                  aria-selected={isActive}
                  aria-controls={`experience-panel-${index}`}
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => setActiveIndex(index)}
                  className={`relative flex-none rounded-xl px-4 py-3.5 text-left transition-colors duration-300
                              lg:w-full ${
                                isActive
                                  ? 'bg-white shadow-card dark:bg-white/[0.06] dark:shadow-none'
                                  : 'hover:bg-white/60 dark:hover:bg-white/[0.03]'
                              }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="experience-marker"
                      aria-hidden="true"
                      className="absolute inset-y-2 left-0 hidden w-0.5 rounded-full bg-blue-500 lg:block"
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    />
                  )}
                  <span
                    className={`block whitespace-nowrap text-sm font-semibold transition-colors ${
                      isActive
                        ? 'text-slate-900 dark:text-white'
                        : 'text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    {role.company}
                  </span>
                  <span className="mt-0.5 block font-mono text-[11px] text-slate-400">{role.period}</span>
                </button>
              );
            })}
          </div>
        </ScrollReveal>

        {/* Detail panel */}
        <ScrollReveal direction="up" delay={0.08}>
          <AnimatePresence mode="wait">
            <motion.div
              key={active.company}
              id={`experience-panel-${activeIndex}`}
              role="tabpanel"
              aria-labelledby={`experience-tab-${activeIndex}`}
              tabIndex={0}
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="surface p-7 sm:p-9 dark:border-emerald-500/15"
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400">
                {active.period}
              </p>
              <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl dark:text-white">
                {active.role}
                <span className="text-emerald-500"> @ {active.company}</span>
              </h3>

              <p className="mt-4 text-[15px] leading-relaxed text-slate-600 dark:text-slate-400">
                {active.summary}
              </p>

              <h4 className="mt-8 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Highlights
              </h4>
              <ul className="mt-4 space-y-3">
                {active.achievements.map((achievement) => (
                  <li key={achievement} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-md
                                 bg-emerald-500/10 text-emerald-500"
                    >
                      <FaArrowUp className="h-2.5 w-2.5" />
                    </span>
                    <span className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                      {achievement}
                    </span>
                  </li>
                ))}
              </ul>

              <ul className="mt-8 flex flex-wrap gap-2 border-t border-slate-200/80 pt-6 dark:border-white/[0.06]">
                {active.tech.map((tech) => (
                  <li
                    key={tech}
                    className="rounded-full border border-slate-200 px-3 py-1 text-[11.5px] font-medium
                               text-slate-600 dark:border-white/[0.08] dark:text-slate-400"
                  >
                    {tech}
                  </li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </ScrollReveal>
      </div>
    </Section>
  );
}
